package com.essntl.features.invoice.ui.allinvoice

import com.essntl.core.quickbooks.domain.QuickbooksClient
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.core.utils.viewmodel.getDataSuspend
import com.essntl.features.invoice.domain.repository.InvoiceRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AllInvoiceViewModel(
    private val invoiceRepository: InvoiceRepository,
    private val quickbooksClient: QuickbooksClient,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<InvoiceState>,
) : ViewModel {

    fun onEvent(event: InvoiceEvent) {
        when (event) {
            is InvoiceEvent.GetInvoices -> getInvoices(event)
            is InvoiceEvent.DownloadInvoice -> downloadInvoice(event)
            is InvoiceEvent.ClearDownloadInvoice -> clearDownloadInvoice()
        }
    }

    private fun getInvoices(event: InvoiceEvent.GetInvoices) {
        state.update {
            it.copy(
                getAllQuickbooksInvoices = State.loading(it.getAllQuickbooksInvoices.data),
            )
        }

        getData(
            getData = {
                event.itineraryId?.let { id ->
                    invoiceRepository.getByItineraryId(id)
                } ?: invoiceRepository.getByProposalId(event.proposalId)
            },
            getState = { state.value.getAllInvoices },
            setState = { newState -> state.update { it.copy(getAllInvoices = newState) } },
            onSuccess = { invoiceList ->
                getQuickbooksInvoices(
                    ids = invoiceList
                        .mapNotNull { it.qbId }
                        .toSet(),
                )
            },
            onFailure = {
                state.update {
                    it.copy(
                        getAllQuickbooksInvoices = State.failure(),
                    )
                }
            },
        )
    }

    private fun getQuickbooksInvoices(
        ids: Set<String>,
    ) {
        scope.launch {
            val invoiceList = ids
                .map { id ->
                    async {
                        quickbooksClient
                            .getInvoice(id)
                    }
                }
                .awaitAll()
                .mapNotNull { it.getOrNull() }

            state.update {
                it.copy(
                    getAllQuickbooksInvoices = State.success(invoiceList),
                )
            }
        }
    }

    private fun downloadInvoice(event: InvoiceEvent.DownloadInvoice) {
        scope.launch {
            state.update {
                it.copy(
                    loadingDownloadQuickbookIds = it.loadingDownloadQuickbookIds + event.qb.Id,
                )
            }

            getDataSuspend(
                getData = {
                    quickbooksClient
                        .downloadInvoicePdf(event.qb.Id)
                        .map {
                            it to event.qb
                        }
                },
                getState = { state.value.downloadQuickbooksInvoice },
                setState = { newState -> state.update { it.copy(downloadQuickbooksInvoice = newState) } },
            )

            state.update {
                it.copy(
                    loadingDownloadQuickbookIds = it.loadingDownloadQuickbookIds - event.qb.Id,
                )
            }
        }
    }

    private fun clearDownloadInvoice() {
        state.update {
            it.copy(
                downloadQuickbooksInvoice = State(),
            )
        }
    }
}